import { x } from '@xstyled/styled-components';
import React, { useState } from 'react';
import { t } from 'ttag';
import { usePostCommentMutation } from '~/data';
import { Button } from '~/ui/buttons';
import { PickList } from '~/ui/pick-list/PickList';
import { Spinner } from '~/ui/spinner/Spinner';
import { Sequence, useTrainingStore } from '../../state';
import { useTrainingParams } from '../../useTrainingParams';
import { getUA } from 'react-device-detect';

interface Props {
  topics?: {
    key: string;
    label: string;
  }[];
  recipient: 'easyllama' | 'company';
}

export const IssueForm = ({ topics, recipient }: Props) => {
  const [question, setQuestion] = useState('');
  const { learnerTrainingId, sequenceId } = useTrainingParams();
  const items = useTrainingStore(s => s.items);
  const [topic, setTopic] = useState(topics && topics[0]);
  const [postComment, { called, loading, data, error }] =
    usePostCommentMutation();

  const handleClick = () => {
    const sequence = items.find(
      i => i.kind === 'sequence' && i.id === sequenceId
    ) as Sequence;

    postComment({
      variables: {
        learnerTrainingUuid: learnerTrainingId!,
        question,
        recipient,
        chapterSequenceId: sequence.chapterSequenceId,
        topic: topic?.key,
        userAgent: getUA
      }
    });
  };

  const isError = Boolean(error || data?.comment?.errors.length);
  const isSuccess = Boolean(called && !loading && !isError);
  const statusMessage = isError
    ? t`We're sorry, an unexpected error has occurred`
    : isSuccess
      ? t`Your message was sent successfully.`
      : '';

  return (
    <x.div
      boxSizing="border-box"
      style={{
        // Prefer natural content width (was minWidth 332px, could grow).
        // Cap at parent so 200% zoom does not clip horizontally (EL–E18).
        minWidth: 'min(332px, 100%)',
        width: 'max-content',
        maxWidth: '100%'
      }}
    >
      {/*
        CAT-1220 / WCAG 4.1.3: live region must exist on first paint (empty),
        then receive text. Mounting aria-live together with the message often
        skips screen-reader announcements.
      */}
      <div role="status" aria-live="polite" aria-atomic="true">
        {statusMessage}
      </div>

      {loading ? <Spinner /> : null}

      {!called && !loading ? (
        <>
          {topics && (
            <x.div>
              <PickList
                items={topics}
                selected={topic}
                labelField="label"
                valueField="key"
                onSelect={t => setTopic(t)}
              />
            </x.div>
          )}
          <x.label htmlFor="writeYourMessage">
            {t`Write your message`}.{' '}
            <x.span color="redDark">* {t`required`}</x.span>
          </x.label>
          <x.textarea
            id="writeYourMessage"
            h="130px"
            maxH="30vh"
            w="100%"
            p="sm"
            resize="vertical"
            mt="20px"
            boxSizing="border-box"
            onChange={e => setQuestion(e.target.value)}
            className="textareaStyleForDarTheme"
            required
          />
          <Button
            disabled={!question.length}
            onClick={handleClick}
          >{t`Send`}</Button>
        </>
      ) : null}
    </x.div>
  );
};
